import { Injectable } from '@angular/core';
import { HelperService } from 'src/app/services/utils/helper.service';
import { UserService } from 'src/app/services/user/user.service';
import { ResourceManagerService } from 'src/app/services/resourceManager/resource-manager.service';
import { LOV_KEYS, HRCHY_KEYS } from 'src/app/dataModels/lovs';
/**
 * Print Service
 */
@Injectable({
  providedIn: 'root'
})
export class PrintService {
  /**
   * logged in user
   */
  user;

  /**
   * constructor to inject dependency
   * @param helperService instance of helperService
   * @param userService instance of userService
   * @param resourceManagerService instance of resourceManagerService
   */
  constructor(
    private helperService: HelperService,
    private userService: UserService,
    private resourceManagerService: ResourceManagerService
  ) {
    this.user = this.userService.getUser();
  }

  /**
   * print patient
   * @param contentHtml html content to be printed
   * @param patientData patient data to be embedded above scheduler details
   */
  printPatientForms(contentHtml: string, patientData) {
    const gender = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.GENDER, patientData.gender, 'descr');

    let patientDataHtml = `<div class="card p-0 border-0 mb-2"><div class="card-body"><table class='table table-sm table-bordered'>`;
    // date being printed
    patientDataHtml += `<tr><th colspan='3'>${this.helperService.formatDate(new Date(), true)}</th>`;

    // user name printing
    patientDataHtml += `<th colspan='3'>Printed By: ${this.user?.userFullName}</th></tr >`;

    // NJIIS header
    patientDataHtml += `<tr><th colspan='6' class='text-center'>New Jersey Immunization Information System</th></tr>`;

    // patient lastName, firstName
    patientDataHtml += `<tr><th>Name</th><td>${patientData.lastName}, ${patientData.firstName}</td>`;

    // patient date of birth
    patientDataHtml += `<th>DOB</th><td>${patientData.dateOfBirth}</td>`;

    // patient age
    patientDataHtml += `<th>Age</th><td>${this.helperService.formatAge(
      this.helperService.calculateAge(patientData.dateOfBirth)
    )}</td></tr>`;

    // patient gender
    patientDataHtml += `<tr><th>Gender</th><td>${gender}</td>`;

    // patient registryId
    patientDataHtml += `<th>Registry Id</th><td>${patientData.registryId}</td>`;

    // patient patientId
    patientDataHtml += `<th>Patient Id</th><td>${patientData.patientId || ''}</td></tr>`;

    // patient medical facility name
    patientDataHtml += `<tr><th colspan='2'>Patient Medical Facility</th><td colspan='4'>${patientData.primaryFacilityName}</td></tr>`;

    // patient insurance name
    patientDataHtml += `<tr><th colspan='2'>Insurance Name</th><td colspan='4'>${this.resourceManagerService.getLovBasedOnHierarchyApi(
      HRCHY_KEYS.INSURANCE_HIERARCHY,
      patientData.insurancePolicy,
      patientData.insuranceProvider,
      'insuranceProviderName',
      'insuranceProvider'
    )}</td></tr>`;

    patientDataHtml += `</table></div></div>`;

    this.print(`${patientDataHtml}${contentHtml}`);
  }

  /**
   * export data to excel file
   * @param title title of the excel
   * @param data data to be exported
   * @param headers headers for the data
   * @param additionalData additional data to be included above the data
   * @param subTitle subTitle for the excel
   */
  printTableData(title: string, data: any[], headers: any[], additionalData: any[], subTitle: string) {
    // header row with 'text' attribute from the headers
    const headerRow = [];
    // for header list, create headerRow and mergedCells data
    headers.forEach((h, i) => {
      if (this.helperService.isHeaderPartOfExport(h)) {
        headerRow.push(h.text);
      }
    });
    const numberOfColumns = this.getNumberOfColumns(headerRow, additionalData);
    let html = null;

    html = `<div class="card p-0 border-0 mb-2"><div class="card-body"><table class='table table-sm table-bordered table-striped'>`;
    // date being printed
    html += `<tr><th colspan='${numberOfColumns / 2}'>${this.helperService.formatDate(new Date(), true)}</th>`;

    // user name printing
    html += `<th colspan='${numberOfColumns / 2}'>Printed By: ${this.user?.userFullName}</th></tr >`;

    // NJIIS header
    html += `<tr><th colspan='${numberOfColumns}' class='text-center'>New Jersey Immunization Information System`;

    if (!this.helperService.IsNullOrEmpty(title)) {
      html += `<br/> ${title}`;
    }

    if (!this.helperService.IsNullOrEmpty(subTitle)) {
      html += `<br/> ${subTitle}`;
    }

    html += `</th></tr>`;

    if (additionalData.length > 0) {
      for (const ad of additionalData) {
        html += `<tr>`;
        Object.keys(ad).forEach((d, i) => {
          const cellValue = ad[d];
          if (i === ad.length - 1) {
            if (i % 2 === 0) {
              html += `<td colspan='${numberOfColumns - ad.length + 1}'>${cellValue}</td>`;
            } else {
              html += `<th colspan='${numberOfColumns - ad.length + 1}'>${cellValue}</th>`;
            }
          } else {
            if (i % 2 === 0) {
              html += `<td>${cellValue}</td>`;
            } else {
              html += `<th>${cellValue}</th>`;
            }
          }
        });
        html += `</tr>`;
      }
    }

    html += `</table></div></div>`;

    html += `<div class="card p-0 border-0 mb-2"><div class="card-body"><table class='table table-sm table-bordered table-striped'>`;

    html += `<tr>`;

    for (const header of headerRow) {
      html += `<th>${header}</th>`;
    }

    html += `</tr>`;

    for (const d of data) {
      html += `<tr>`;
      for (const h of headers) {
        if (this.helperService.isHeaderPartOfExport(h)) {
          let cellValue = d[h.field];
          if (h.format === 'PHONE') {
            cellValue = this.helperService.formatPhone(cellValue);
          }
          if (!this.helperService.IsNullOrEmpty(cellValue)) {
            html += `<td>${cellValue.toString().replace(/\n/g, '<br/>')}</td>`;
          } else {
            html += `<td>&nbsp;</td>`;
          }
        }
      }
      html += `</tr>`;
    }

    html += `</table></div></div>`;

    this.print(html);
  }

  /**
   * get number of columns the excel has
   * @param headerRow headerRow
   * @param additionalData additionalData
   */
  private getNumberOfColumns(headerRow, additionalData) {
    let numberOfColumns = headerRow.length;

    for (const data of additionalData) {
      numberOfColumns = numberOfColumns > data.length ? numberOfColumns : data.length;
    }
    return numberOfColumns;
  }

  /**
   * print html
   * @param contentHtml html
   */
  print(contentHtml) {
    
    let html = `<!DOCTYPE html><html>`;
    html += `<head>${document.head.innerHTML}</head>`;
    html += `<body onload='window.print();' onafterprint='window.close();'>${contentHtml}</body></html>`;
   // console.log(html);
    const WindowPrt = window.open('', '_blank', 'width=900,height=900');
    WindowPrt.document.write(html);
    WindowPrt.document.close();
    WindowPrt.focus();
  }

  /**
   * print form content
   * @param formId form id
   * @param additionalHtml additional html to be added above the html
   */
  printForm(formId, additionalHtml?) {
    const elements = Array.from(document.forms[formId].elements);
    for (let i = 0; i < elements.length; i++) {
      switch (elements[i].getAttribute('type') || (elements[i] as HTMLInputElement).type) {
        case 'text': {
          const element = elements[i] as HTMLInputElement;
          elements[i].setAttribute('value', element.value);
          break;
        }
        case 'textarea': {
          const element = elements[i] as HTMLInputElement;
          elements[i].innerHTML = element.value;
          break;
        }
      }
    }

    const formHtml = document.getElementById(formId).innerHTML;
    if (additionalHtml) {
      this.print(`${additionalHtml}${formHtml}`);
    } else {
      this.print(formHtml);
    }
  }

  /**
   * print all forms in view
   */
  printAllForms() {
    let formHtml = '';
    const forms = document.forms;
    for (let i = 1; i < forms.length; i++) {
      for (let j = 0; j < forms[i].elements.length; j++) {
        switch (forms[i].elements[j].getAttribute('type') || (forms[i].elements[j] as HTMLInputElement).type) {
          case 'text': {
            const element = forms[i].elements[j] as HTMLInputElement;
            forms[i].elements[j].setAttribute('value', element.value);
            break;
          }
          case 'textarea': {
            const element = forms[i].elements[j] as HTMLInputElement;
            forms[i].elements[j].innerHTML = element.value;
            break;
          }
        }
      }
      formHtml += forms[i].innerHTML;
    }
    this.print(formHtml);
  }

  /**
   * print avery label
   * @param patientList list of selected patients
   */
  printAveryLabel(patientList) {
    let documentBuilder = `<!DOCTYPE html><html><head><title></title><style>${this.getAveryCss()}</style>`;
    documentBuilder += `</head><body onload='window.print();window.close()'>`;
    documentBuilder += this.getAveryLabel(patientList) + '</body></html>';
    const newWindow = window.open('', '_blank', 'width=600,height=900');
    newWindow.document.write(documentBuilder);
    newWindow.document.close();
    newWindow.focus(); // necessary for IE >= 10
  }

  /**
   * avery css
   */
  getAveryCss() {
    return `@page {
        size: portrait;
        margin-top: 0.5in;
        margin-left: 0.1875in;
        margin-right: 0.1875in;
        font-size: 8pt;
    }
    body {
        width: 8.5in;
        margin: 0;
        font-size: 8pt;
    }
    tr {
        width: 100%;
        margin: 0;
    }
    td.label {
        font-size: 10px;
        font-family: sans-serif;
        width: 2.094in;
        height: 0.875in;
        padding: 0.125in 0.3in 0;
        margin-right: 0.125in;
        /* the gutter */
        float: left;
        text-align: center;
        overflow: hidden;
    }
    td.separator {
        width: 0.12in;
    }
    .page-break {
        clear: left;
        display: block;
        page-break-after: always;
    }
    .table {
        table-layout: fixed;
        width: 100%;
    }`;
  }

  /**
   * get the patient details in avery label format
   * @param patientList list of patients
   */
  private getAveryLabel(patientList) {
    let labelhtml = '<table class="table">';
    let rowIndex = 0;
    let pageIndex = 0;
    labelhtml += '<tr>';
    for (const patient of patientList) {
      labelhtml += "<td class='label'>To the Parent/Guardian of<br/>";
      labelhtml += '<div>' + patient.firstName;
      if (!this.helperService.IsNullOrEmpty(patient.middleName)) {
        labelhtml += ' ' + patient.middleName;
      }
      if (!this.helperService.IsNullOrEmpty(patient.lastName)) {
        labelhtml += ' ' + patient.lastName;
      }
      labelhtml += '</div><div>' + patient.addressLine1;
      if (!this.helperService.IsNullOrEmpty(patient.addressLine2)) {
        labelhtml += '</div><div>' + patient.addressLine2;
      }
      labelhtml += '</div><div>' + patient.city + ', ' + patient.state + ', ' + patient.zip;

      labelhtml += '</div></td>';

      if (pageIndex === 29) {
        pageIndex = 0;
        rowIndex = -1;
        labelhtml += `</tr></table><div class='page-break'></div><table><tr>`;
      } else {
        pageIndex++;
      }

      if (rowIndex === 2) {
        rowIndex = 0;
        labelhtml += '</tr><tr>';
      } else {
        rowIndex++;
      }
    }
    labelhtml += '</table>';
    return labelhtml;
  }
}
